import * as React from 'react';
import { useLocation, useNavigate } from "react-router-dom";
import { Box, TextInput, Switch, Group, Button, Textarea, NumberInput } from '@mantine/core'
import { useForm, isNotEmpty } from "@mantine/form";
import axios from 'axios';
import type { IResponse } from "../../types/api";
import { CLIENT_ROUTE, API_ROUTE, CLIENT_TITLE } from "./constants";
import type { ICategory } from "./types";
import FileUpload from "../../components/file-upload";
import { FileWithPath } from "@mantine/dropzone";
import { showMessage } from "../../lib/notification";
import { IMAGE_MIME_TYPE } from "@mantine/dropzone";
import Page from "../../components/layout/Page";
import { useFormStyles } from "../../styles/form";
import { assignFiles } from "../../utils/form";

interface FormValues extends Pick<ICategory, 'code' | 'title' | 'description' | 'order' | 'isFeatured' | 'isActive' | 'categoryNumber'> {
    cover: string | FileWithPath | null;
}

interface IParams extends Omit<FormValues, 'cover'> {
    cover?: string;
}

const EditCategory = () => {

    const { state: id = null } = useLocation();

    const { classes } = useFormStyles();
    const navigate = useNavigate();

    const form = useForm<FormValues>({
        initialValues: {
            code: '',
            title: '',
            description: '',
            cover: null,
            order: 0,
            isFeatured: false,
            isActive: false,
            categoryNumber: 0,
        },

        validate: {
            title: isNotEmpty('Нэр оруулна уу'),
            code: isNotEmpty('Код оруулна уу'),
        },
    });

    React.useEffect(() => {
        const fetchData = async () => {
            const { data } = await axios.get<IResponse<ICategory>>(`${API_ROUTE}/${id}`);
            form.setValues(data.result);
            form.resetDirty();
        }
        void fetchData();
    }, []);

    const handleSubmit = async (values: typeof form.values) => {

        const files = await assignFiles({
            cover: values.cover
        }, 'category')
        const params: IParams = {
            ...values,
            ...files
        }

        const { data } = await axios.post<IResponse<any>>(`${API_ROUTE}/${id}`, params);

        if (data.statusCode === 200) {
            navigate(CLIENT_ROUTE);
            showMessage('Амжилттай', 'green');
        } else {
            showMessage('Алдаа гарлаа',);
        }
    }

    return (
        <Page>
            <Page.Header title={`${CLIENT_TITLE} засах`} backRoute={CLIENT_ROUTE}/>
            <Page.Body>
                <Box sx={{ maxWidth: 500 }} mx="auto">

                    <form onSubmit={form.onSubmit(handleSubmit)} className={classes.form}>
                        <TextInput
                            withAsterisk
                            autoComplete='off'
                            label="Код"
                            placeholder="Код оруулна уу"
                            {...form.getInputProps('code')}
                        />

                        <TextInput
                            withAsterisk
                            label="Нэр"
                            placeholder="Нэр оруулна уу"
                            {...form.getInputProps('title')}
                        />

                        <NumberInput
                            hideControls
                            label="Ангилал дугаар"
                            placeholder="Ангилал дугаар"
                            {...form.getInputProps('categoryNumber')}
                        />

                        <Textarea
                            label="Тайлбар"
                            placeholder="Тайлбар..."
                            {...form.getInputProps('description')}
                        />

                        <FileUpload
                            value={form.values.cover ?? null}
                            onChange={(value) => {
                                form.setFieldValue('cover', value);
                            }}
                            label='Ковер зураг'
                            accept={IMAGE_MIME_TYPE}
                        />

                        <NumberInput
                            label="Дараалал"
                            placeholder="Дараалал"
                            {...form.getInputProps('order')}
                        />

                        <Switch
                            label="Онцолсон эсэх"
                            {...form.getInputProps('isFeatured', { type: 'checkbox' })}
                        />

                        <Switch
                            label="Идэвхтэй эсэх"
                            {...form.getInputProps('isActive', { type: 'checkbox' })}
                        />
                    </form>
                </Box>
            </Page.Body>
            <Page.Footer>
                <Group position="right" mt="md">
                    <Button color='color-secondary' onClick={() => form.onSubmit(handleSubmit)()}>Хадгалах</Button>
                </Group>
            </Page.Footer>
        </Page>
    );
};

export default EditCategory;